/**
 * Build progression series from completed sessions (sorted by completed_at).
 * Each session's stats JSONField is used to plot evolution over time.
 */
export function buildProgressionSeries(sessions) {
  const winRate = [];
  const expectancy = [];
  const pnl = [];
  const cumulativePnl = [];
  let cumPnl = 0;

  sessions.forEach((s, i) => {
    const stats = s.stats || {};
    const label = s.name || `Session #${s.id}`;
    const date = s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '';

    winRate.push({ x: i + 1, y: stats.win_rate ?? 0, label, date });
    expectancy.push({ x: i + 1, y: stats.expectancy ?? 0, label, date });
    pnl.push({ x: i + 1, y: stats.total_pnl ?? 0, label, date });

    cumPnl += stats.total_pnl ?? 0;
    cumulativePnl.push({ x: i + 1, y: Math.round(cumPnl * 100) / 100, label, date });
  });

  return { winRate, expectancy, pnl, cumulativePnl };
}

/**
 * Compute average session-level stats from completed sessions.
 * Useful for metrics like avg max_drawdown which the trade-level endpoint doesn't provide.
 */
export function computeSessionAverages(sessions) {
  const valid = sessions.filter((s) => s.stats && s.stats.total_trades > 0);
  if (valid.length === 0) return null;

  const sum = (key) => valid.reduce((s, ses) => s + (ses.stats[key] ?? 0), 0);
  const avg = (key) => Math.round((sum(key) / valid.length) * 100) / 100;

  return {
    session_count: valid.length,
    avg_win_rate: avg('win_rate'),
    avg_expectancy: avg('expectancy'),
    avg_max_drawdown: avg('max_drawdown'),
    avg_profit_factor: avg('profit_factor'),
    total_pnl: Math.round(sum('total_pnl') * 100) / 100,
    total_trades: sum('total_trades'),
  };
}

/**
 * Format a metric value for display.
 */
export function formatMetric(value, type) {
  if (value == null || value === undefined) return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(n)) return '—';

  switch (type) {
    case 'percent':
      return `${n.toFixed(1)}%`;
    case 'pnl':
      return `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
    case 'ratio':
      return n.toFixed(2);
    case 'count':
      return String(Math.round(n));
    default:
      return n.toFixed(2);
  }
}
